import React, { useState, useEffect, useRef } from 'react';
import { useNotify } from 'react-admin';
import { useFormState } from 'react-final-form';
import { useInput } from 'react-admin';
import { Box, Switch, FormControlLabel } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { authServer } from '../../auth/keycloak';
import * as R from 'ramda';

const apiUrl = process.env.REACT_APP_BACKEND_HOST;

const ERROR_MESSAGES = {
  ZOOM_URL_MISSING: 'Missing required field zoom_url',
  INVALID_ZOOM_URL_FORMAT:
    'Invalid Zoom URL format. Expected https://*.zoom.us/j/{meetingId}',
  ZOOM_URL_IN_USE: 'Zoom URL already in use by another cohort',
  ZOOM_MEETING_NOT_FOUND: 'Zoom meeting not found for the provided meetingId',
  PERSONAL_MEETING_ID:
    'Zoom session is a Personal Meeting ID (PMI), not allowed',
  AUTH_REQUIRED: 'Zoom meeting authentication is not enabled',
  AUTH_PROFILE_INVALID:
    'Zoom meeting does not use the "Login with Ironhack account" authentication profile',
  INTERNAL_ERROR: 'An internal error occurred. Please try again',
};

const validateUrl = (url) =>
  R.pipe(
    R.defaultTo(''),
    R.trim,
    R.both(
      R.startsWith('https://ironhack.zoom.us/j/'),
      R.pipe(R.replace('https://ironhack.zoom.us/j/', ''), R.test(/^\d+$/))
    )
  )(url);

const AutomaticAttendanceInputCreate = ({
  source,
  defaultValue,
  record,
  ...props
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const notify = useNotify();
  const { submitSucceeded, values } = useFormState();

  const inputResult = useInput({ source, defaultValue: defaultValue || false });
  const { input } = inputResult || {};
  const value = input?.value ?? defaultValue ?? false;
  const onChange = input?.onChange ?? (() => {});
  const [lastValidatedUrl, setLastValidatedUrl] = useState(null);
  const currentZoomUrl = (values?.zoom_url || '').trim();
  const alertRef = useRef(null);

  useEffect(() => {
    if (value !== undefined) {
    }
  }, [value]);

  useEffect(() => {
    if (submitSucceeded) {
      // no-op for now
    }
  }, [submitSucceeded]);

  const handleChange = async (event, checked) => {
    if (!checked) {
      setErrorMessages([]);
      onChange(false);
      setLastValidatedUrl(null); // Reset last validated URL when unchecked
      return;
    }
    setIsValidating(true);
    setErrorMessages([]);

    try {
      const zoomUrl = currentZoomUrl;

      if (!zoomUrl || !zoomUrl.trim()) {
        setErrorMessages([
          'Zoom URL is required to enable Automatic Attendance',
        ]);
        setIsValidating(false);
        return;
      }

      const response = await fetch(`${apiUrl}/cohorts/zoom-session/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authServer.token}`,
        },
        body: JSON.stringify({ zoom_url: zoomUrl }),
      });

      const responseData = await response.json();

      if (response.ok) {
        setErrorMessages([]);
        setLastValidatedUrl(zoomUrl);
        onChange(true);
        notify('Zoom URL validated successfully', 'success');
      } else {
        let messages = [];
        if (Array.isArray(responseData.errors)) {
          messages = responseData.errors
            .map((e) => ERROR_MESSAGES[e.error_code] || e.error)
            .filter(Boolean);
        } else if (responseData.error_code || responseData.error) {
          const single =
            ERROR_MESSAGES[responseData.error_code] || responseData.error;
          if (single) messages = [single];
        }
        const fallbackMessage = responseData.message || responseData.error;
        if (messages.length === 0 && fallbackMessage)
          messages = [fallbackMessage];
        if (messages.length === 0) messages = ['Validation failed'];

        setErrorMessages(messages);
        setTimeout(() => {
          if (alertRef.current) {
            alertRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 0);
        notify(messages.join(' • '), 'warning');
      }
    } catch (error) {
      const errorMessage =
        error.body?.error || error.message || 'Validation failed';
      setErrorMessages([errorMessage]);
      setTimeout(() => {
        if (alertRef.current) {
          alertRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 0);
      notify(errorMessage, 'error');
    } finally {
      setIsValidating(false);
    }
  };
  const isValidZoomUrl = validateUrl(currentZoomUrl);

  useEffect(() => {
    if (!currentZoomUrl || !validateUrl(currentZoomUrl)) {
      if (value) {
        onChange(false);
      }
      setLastValidatedUrl(null);
      setErrorMessages([]);
      setIsValidating(false);
      return;
    }
    if (value && currentZoomUrl !== lastValidatedUrl) {
      onChange(false);
    }
  }, [currentZoomUrl, value, onChange, lastValidatedUrl]);

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(value)}
            onChange={handleChange}
            disabled={!isValidZoomUrl || !currentZoomUrl || isValidating}
            color="primary"
          />
        }
        label={props.label || 'Automatic Attendance'}
      />
      <Box
        style={{
          marginTop: '12px',
          marginBottom: '16px',
          width: '60%',
          maxWidth: '800px',
          minWidth: '420px',
          minHeight: errorMessages.length > 0 ? undefined : 0,
        }}
        ref={alertRef}
      >
        {errorMessages.length > 0 && (
          <Alert severity="error">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              {errorMessages.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default AutomaticAttendanceInputCreate;
